import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import { bulkSignOff } from "./bulkSignOff";

const OUTCOME_TEXT = {
  SIGNED_OFF: "signed off",
  RESIGNED: "signed off again (sign-off date is now today)",
  SKIPPED: "skipped",
  FAILED: "not signed off",
};

function summary(results) {
  const done = results.filter((r) => r.outcome === "SIGNED_OFF" || r.outcome === "RESIGNED").length;
  const missed = results.length - done;
  if (missed === 0) return { severity: "success", text: `${done} student${done === 1 ? "" : "s"} signed off.` };
  if (done === 0) return { severity: "warning", text: "No students were signed off." };
  return { severity: "warning", text: `${done} signed off, ${missed} not.` };
}

/**
 * The roster's bulk action: signs off the selected students as passed. Shown while students are selected, and
 * afterwards with each student's result. The server checks each student's requirements; anyone who
 * doesn't meet them is skipped and shown with the server's reasons. Certificates still go out only with Send certificates.
 */
export default function BulkSignOffBar({ classId, selected, onClear, onDone, request }) {
  // The students being confirmed, frozen when the dialog opens so it can't change (or read 0) while it closes.
  const [pending, setPending] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  // A sign-off asked for elsewhere on the roster (a row's Sign off, the "Sign off all" banner): open the same
  // confirmation for exactly those students. `request` is { id, students } and a new id opens it again.
  useEffect(() => {
    if (!request?.students?.length) return;
    setResults(null);
    setPending(request.students);
    setConfirming(true);
  }, [request?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setConfirming(false);
    setRunning(true);
    try {
      const outcome = await bulkSignOff(classId, pending);
      setResults(outcome);
      onDone?.(outcome);
    } finally {
      setRunning(false);
    }
  };

  if (selected.length === 0 && !results && !confirming) return null;
  const s = results && summary(results);

  return (
    <Box data-testid="bulk-sign-off" sx={{ mb: 1.5 }}>
      {selected.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", p: 1.25, borderRadius: "10px",
          border: "1px solid var(--color-hairline-cloud, #e6e3ee)", bgcolor: "var(--color-surface-press-light, #f6f5f9)" }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{selected.length} selected</Typography>
          <Button size="small" variant="contained" disabled={running} onClick={() => { setResults(null); setPending(selected); setConfirming(true); }}
            sx={{ textTransform: "none", fontWeight: 700 }}>
            {running ? "Signing off…" : `Sign off ${selected.length}`}
          </Button>
          <Button size="small" disabled={running} onClick={onClear} sx={{ textTransform: "none" }}>Clear</Button>
          {running && <CircularProgress size={16} />}
        </Box>
      )}

      {s && (
        <Alert severity={s.severity} onClose={() => setResults(null)} sx={{ mt: 1 }} data-testid="bulk-sign-off-results">
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{s.text}</Typography>
          {results.map((r) => (
            <Typography key={r.userId} variant="caption" component="div">
              {r.name}: {OUTCOME_TEXT[r.outcome]}{r.reason ? ` — ${r.reason}` : ""}
            </Typography>
          ))}
        </Alert>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Sign off {pending.length} student{pending.length === 1 ? "" : "s"}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {pending.map((st) => st.name).join(", ")} will be signed off as passed. Anyone who hasn't met the
            course requirements is skipped. Students already signed off get today as their sign-off date.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Certificates aren't sent now: they go out when the class is complete and you press Send certificates.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" onClick={run}>Sign off</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
